import { useCallback, useRef, useState } from 'react';
import {
  listConnectors,
  listMcpServers,
  listNativeTools,
} from '../services/api/agents';

const CACHE_TTL_MS = 45_000;

const cache = { data: null, fetchedAt: 0 };

export function invalidateToolsListCache() {
  cache.data = null;
  cache.fetchedAt = 0;
}

function applyCache(setConnectors, setMcpServers, setNativeTools, payload) {
  setConnectors(payload.connectors);
  setMcpServers(payload.mcpServers);
  setNativeTools(payload.nativeTools);
}

export function useToolsListCache(keycloak, { isAdmin = false } = {}) {
  const [connectors, setConnectors] = useState(() => cache.data?.connectors || []);
  const [mcpServers, setMcpServers] = useState(() => cache.data?.mcpServers || []);
  const [nativeTools, setNativeTools] = useState(() => cache.data?.nativeTools || []);
  const [loading, setLoading] = useState(() => !cache.data);
  const inflightRef = useRef(null);
  const isAdminRef = useRef(isAdmin);
  isAdminRef.current = isAdmin;

  const load = useCallback(async (force = false) => {
    if (!keycloak?.authenticated) return;
    const now = Date.now();
    const admin = isAdminRef.current;
    const cacheFresh = cache.data && now - cache.fetchedAt < CACHE_TTL_MS;
    const cacheHasNative = !admin || (cache.data?.nativeTools?.length ?? 0) > 0
      || cache.data?._nativeFetched;

    if (!force && cacheFresh && cacheHasNative) {
      applyCache(setConnectors, setMcpServers, setNativeTools, cache.data);
      setLoading(false);
      return cache.data;
    }

    // Stale-while-revalidate: paint cached rows immediately, refresh in background.
    if (cache.data) {
      applyCache(setConnectors, setMcpServers, setNativeTools, cache.data);
      setLoading(false);
    } else {
      setLoading(true);
    }

    if (inflightRef.current) return inflightRef.current;

    const promise = (async () => {
      try {
        const nativePromise = admin
          ? listNativeTools(keycloak).catch((err) => {
              console.warn('Failed to list native tools', err);
              return [];
            })
          : Promise.resolve([]);
        const [httpList, mcpList, nativeList] = await Promise.all([
          listConnectors(keycloak),
          listMcpServers(keycloak),
          nativePromise,
        ]);
        const payload = {
          connectors: httpList || [],
          mcpServers: mcpList || [],
          nativeTools: nativeList || [],
          _nativeFetched: admin,
        };
        cache.data = payload;
        cache.fetchedAt = Date.now();
        applyCache(setConnectors, setMcpServers, setNativeTools, payload);
        return payload;
      } finally {
        setLoading(false);
        inflightRef.current = null;
      }
    })();
    inflightRef.current = promise;
    return promise;
  }, [keycloak]);

  return {
    connectors,
    mcpServers,
    nativeTools,
    loading,
    load,
    invalidate: invalidateToolsListCache,
  };
}
